/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Calendar, MapPin, Clock, ArrowRight, Camera, X, ChevronLeft, ChevronRight, Image } from 'lucide-react';
import { Button } from '../components/ui/button';
import LeadershipSignature from '../components/LeadershipSignature';

import Img from '../components/Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatDate = (dateStr) => {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};
const formatDateShort = (dateStr) => {
  const d = new Date(dateStr + 'T12:00:00');
  return { day: d.getDate(), month: d.toLocaleString('en', { month: 'short' }), year: d.getFullYear() };
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(-1);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
    } catch { /* */ }
  };

  const today = new Date().toISOString().split('T')[0];
  const upcoming = events.filter(e => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const past = events.filter(e => e.date < today).sort((a, b) => b.date.localeCompare(a.date));

  // Lightbox controls
  const galleryImages = selectedEvent?.gallery_images || [];
  const openGallery = (idx) => setGalleryIndex(idx);
  const closeGallery = () => setGalleryIndex(-1);
  const nextImage = () => setGalleryIndex(i => (i + 1) % galleryImages.length);
  const prevImage = () => setGalleryIndex(i => (i - 1 + galleryImages.length) % galleryImages.length);

  // Event detail view
  if (selectedEvent) {
    const ev = selectedEvent;
    const isPast = ev.date < today;
    const gallery = ev.gallery_images || [];
    return (
      <div className="min-h-screen pt-16 sm:pt-20 bg-slate-50">
        {/* Cover / Flyer Hero */}
        <div className="relative h-56 sm:h-72 lg:h-96 bg-[#002249] overflow-hidden" data-testid="event-detail-hero">
          {(ev.flyer_url || ev.image_url) ? (
            <>
              <Img variant="event" src={resolveImg(ev.flyer_url || ev.image_url)} alt={ev.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            </>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#002249] via-[#0a3570] to-[#1D3557]">
              <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </div>
          )}
          {/* Back button */}
          <button onClick={() => setSelectedEvent(null)} className="absolute top-4 left-4 sm:top-6 sm:left-6 flex items-center gap-1.5 text-white/90 text-sm font-medium bg-black/30 hover:bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-lg transition-colors z-10" data-testid="event-back-btn">
            <ChevronLeft className="w-4 h-4" /> All Events
          </button>
          {/* Date badge */}
          <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 z-10">
            {isPast && <span className="text-xs font-bold bg-white/20 backdrop-blur-sm text-white px-2.5 py-1 rounded-full mb-2 inline-block">Past Event</span>}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mt-1">{ev.title}</h1>
          </div>
        </div>

        {/* Event Content */}
        <div className="container-custom py-8 sm:py-12">
          <div className="max-w-4xl mx-auto">
            {/* Details Card */}
            <div className="bg-white rounded-xl shadow-sm p-5 sm:p-8 mb-6 sm:mb-8" data-testid="event-detail-card">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#002249]/10 flex items-center justify-center flex-shrink-0"><Calendar className="w-5 h-5 text-[#002249]" /></div>
                  <div className="min-w-0"><p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wide">Date</p><p className="text-sm font-semibold text-[#050505] mt-0.5">{formatDate(ev.date)}</p></div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#E63946]/10 flex items-center justify-center flex-shrink-0"><Clock className="w-5 h-5 text-[#E63946]" /></div>
                  <div className="min-w-0"><p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wide">Time</p><p className="text-sm font-semibold text-[#050505] mt-0.5">{ev.time}</p></div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-green-600" /></div>
                  <div className="min-w-0"><p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wide">Location</p><p className="text-sm font-semibold text-[#050505] mt-0.5 break-words">{ev.location}</p></div>
                </div>
              </div>
              <div className="border-t border-slate-100 pt-6">
                <h3 className="text-lg font-bold text-[#002249] mb-3">About This Event</h3>
                <p className="text-slate-600 leading-relaxed whitespace-pre-line">{ev.description}</p>
              </div>
              {isPast && ev.recap && (
                <div className="border-t border-slate-100 pt-6 mt-6">
                  <h3 className="text-lg font-bold text-[#002249] mb-3">Event Recap</h3>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line">{ev.recap}</p>
                </div>
              )}
              {isPast && ev.recap_blog_id && (
                <div className="border-t border-slate-100 pt-6 mt-6">
                  <Link
                    to="/news"
                    className="inline-flex items-center gap-2 bg-[#002249] hover:bg-[#001833] text-white font-semibold text-sm px-6 py-3 rounded-lg transition-colors shadow-sm"
                    data-testid={`view-recap-link-${ev.id}`}
                  >
                    Read Full Recap <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
              {ev.registration_link && !isPast && (
                <div className="border-t border-slate-100 pt-6 mt-6">
                  <a href={ev.registration_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#D32F3F] text-white font-semibold text-sm px-6 py-3 rounded-lg transition-colors shadow-sm">
                    Register Now <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              )}
            </div>

            {/* Photo Gallery */}
            {gallery.length > 0 && (
              <div className="mb-8" data-testid="event-gallery">
                <h3 className="text-lg font-bold text-[#002249] mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5" /> Event Photos <span className="text-sm font-normal text-slate-400">({gallery.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {gallery.map((img, i) => (
                    <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}
                      className="aspect-square rounded-lg overflow-hidden cursor-pointer group bg-slate-100" onClick={() => openGallery(i)} data-testid={`gallery-photo-${i}`}>
                      <Img variant="generic" src={resolveImg(img)} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Flyer display (if different from cover) */}
            {ev.flyer_url && ev.image_url && ev.flyer_url !== ev.image_url && (
              <div className="mb-8" data-testid="event-flyer-section">
                <h3 className="text-lg font-bold text-[#002249] mb-4">Event Flyer</h3>
                <div className="max-w-md mx-auto rounded-xl overflow-hidden shadow-lg">
                  <Img variant="event" src={resolveImg(ev.flyer_url)} alt="Event Flyer" className="w-full" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Gallery Lightbox */}
        <AnimatePresence>
          {galleryIndex >= 0 && galleryImages.length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center" onClick={closeGallery} data-testid="gallery-lightbox">
              <button className="absolute top-4 right-4 text-white/70 hover:text-white z-10 p-2" onClick={closeGallery}><X className="w-7 h-7" /></button>
              {galleryImages.length > 1 && (
                <>
                  <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 z-10" onClick={e => { e.stopPropagation(); prevImage(); }}><ChevronLeft className="w-8 h-8" /></button>
                  <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 z-10" onClick={e => { e.stopPropagation(); nextImage(); }}><ChevronRight className="w-8 h-8" /></button>
                </>
              )}
              <Img variant="generic" src={resolveImg(galleryImages[galleryIndex])} alt="" className="max-w-[90vw] max-h-[85vh] object-contain" onClick={e => e.stopPropagation()} />
              <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-sm">{galleryIndex + 1} / {galleryImages.length}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Main events listing
  return <EventsListing
    events={events}
    upcoming={upcoming}
    past={past}
    onSelect={setSelectedEvent}
  />;
};

/* ─── REDESIGNED LISTING ──────────────────────────────────────────────────── */
const EventsListing = ({ events, upcoming, past, onSelect }) => {
  const [filter, setFilter] = React.useState('upcoming');
  const [showAll, setShowAll] = React.useState(false);

  const featured = upcoming.filter(e => e.is_featured && !e.is_external).slice(0, 3);
  const upcomingChamber = upcoming.filter(e => !e.is_external);
  const upcomingNonFeatured = upcomingChamber.filter(e => !featured.find(f => f.id === e.id));
  const externalEvents = events.filter(e => e.is_external && e.date >= new Date().toISOString().split('T')[0])
    .sort((a, b) => a.date.localeCompare(b.date));

  const FILTERS = [
    { key: 'upcoming', label: 'Upcoming', count: upcomingChamber.length },
    { key: 'featured', label: 'Featured', count: featured.length },
    { key: 'external', label: 'Recommended', count: externalEvents.length },
    { key: 'past', label: 'Past', count: past.length },
  ];

  const visibleList = (() => {
    if (filter === 'upcoming') return upcomingNonFeatured;
    if (filter === 'featured') return featured;
    if (filter === 'external') return externalEvents;
    return past;
  })();

  const visibleSlice = showAll ? visibleList : visibleList.slice(0, 6);

  return (
    <div className="min-h-screen pt-14 sm:pt-20 bg-slate-50">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#002249]" data-testid="events-hero">
        <div className="absolute inset-0 opacity-[0.08]"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 60%, #E63946 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#002249] via-[#001530] to-[#0a3570]" style={{ mixBlendMode: 'multiply' }} />
        <div className="relative container-custom py-14 sm:py-20">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="text-[10.5px] sm:text-xs font-bold text-amber-300 tracking-[0.18em] uppercase mb-3 block">DCCF Network · Events</span>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-4">
              Events
            </h1>
            <p className="text-white/80 text-base sm:text-lg lg:text-xl font-light leading-relaxed">
              <span className="text-amber-300 font-semibold">Connect.</span> <span className="text-amber-300 font-semibold">Learn.</span> <span className="text-amber-300 font-semibold">Grow.</span>
              <br className="hidden sm:block" />
              <span className="text-white/65 text-sm sm:text-base block sm:inline mt-2 sm:mt-0">Live gatherings, networking nights, masterclasses & member-only roundtables.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* STICKY FILTERS */}
      <div className="sticky top-14 sm:top-16 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200" data-testid="events-filters">
        <div className="container-custom">
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto py-3 scrollbar-hide">
            {FILTERS.map((f) => (
              <motion.button
                key={f.key}
                onClick={() => { setFilter(f.key); setShowAll(false); }}
                whileTap={{ scale: 0.96 }}
                className={`relative inline-flex items-center gap-2 h-10 px-4 sm:px-5 rounded-full text-[13px] font-semibold whitespace-nowrap transition-all ${
                  filter === f.key
                    ? 'bg-[#002249] text-white shadow-[0_4px_14px_-4px_rgba(0,34,73,0.4)]'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
                <span className={`text-[10.5px] font-bold px-1.5 rounded-full ${filter === f.key ? 'bg-white/20' : 'bg-white text-slate-500'}`}>
                  {f.count}
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      <div className="container-custom py-8 sm:py-12">
        <div className="grid lg:grid-cols-[1fr_320px] gap-8">
          <div className="min-w-0">
            {/* FEATURED — only when on Upcoming or Featured filter */}
            {(filter === 'upcoming' || filter === 'featured') && featured.length > 0 && (
              <section className="mb-10" data-testid="featured-section">
                <div className="flex items-end justify-between mb-4 sm:mb-5">
                  <div>
                    <span className="text-[10.5px] sm:text-xs font-bold text-[#E63946] tracking-[0.16em] uppercase block mb-1">Highlighted</span>
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#002249] tracking-tight">Featured Events</h2>
                  </div>
                </div>
                <div className={`grid gap-5 ${featured.length === 1 ? '' : featured.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
                  {featured.map((event, i) => (
                    <FeaturedCard key={event.id} event={event} onSelect={onSelect} index={i} large={featured.length === 1} />
                  ))}
                </div>
              </section>
            )}

            {/* MAIN LIST */}
            <section className="mb-10" data-testid={`section-${filter}`}>
              <div className="flex items-end justify-between mb-4 sm:mb-5">
                <div>
                  <span className="text-[10.5px] sm:text-xs font-bold text-[#E63946] tracking-[0.16em] uppercase block mb-1">
                    {filter === 'past' ? 'Memories' : filter === 'external' ? 'Curated' : 'Coming Up'}
                  </span>
                  <h2 className="text-xl sm:text-2xl font-bold text-[#002249] tracking-tight">
                    {filter === 'upcoming' && 'All Upcoming Events'}
                    {filter === 'featured' && 'Featured Events'}
                    {filter === 'external' && 'Recommended Events'}
                    {filter === 'past' && 'Past Events & Recaps'}
                  </h2>
                </div>
              </div>

              {visibleSlice.length === 0 ? (
                <EmptyEventsState filter={filter} />
              ) : (
                <>
                  <div className={filter === 'past' ? 'grid sm:grid-cols-2 gap-5' : 'grid sm:grid-cols-2 gap-5'}>
                    {visibleSlice.map((event, idx) => (
                      filter === 'past'
                        ? <PastRecapCard key={event.id} event={event} onSelect={onSelect} index={idx} />
                        : filter === 'external'
                        ? <ExternalEventCard key={event.id} event={event} index={idx} />
                        : <UpcomingCard key={event.id} event={event} onSelect={onSelect} index={idx} />
                    ))}
                  </div>
                  {visibleList.length > 6 && !showAll && (
                    <div className="flex justify-center mt-8">
                      <motion.button
                        whileHover={{ y: -1 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => setShowAll(true)}
                        className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white border border-slate-200 hover:border-[#002249] text-[#002249] text-[13.5px] font-semibold shadow-sm hover:shadow-md transition-all"
                        data-testid="load-more-btn"
                      >
                        Load more events <ArrowRight className="w-4 h-4" />
                      </motion.button>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-5 lg:sticky lg:top-32 lg:self-start" data-testid="events-sidebar">
            {/* Submit your event CTA */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#002249] to-[#001530] text-white p-6"
            >
              <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-[#E63946]/20 blur-2xl" />
              <div className="relative">
                <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-amber-300">Hosting an event?</span>
                <h3 className="text-[18px] font-bold tracking-tight mt-1.5">Submit Your Event</h3>
                <p className="text-[12.5px] text-white/70 mt-2 leading-relaxed">
                  Share workshops, mixers, and member-led gatherings with the DCCF community.
                </p>
                <Link to="/contact" className="mt-4 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#E63946] hover:bg-[#c02a35] text-white text-[12.5px] font-semibold transition-colors" data-testid="submit-event-cta">
                  Get in touch <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.div>

            {/* Upcoming highlights mini list */}
            {upcomingChamber.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl border border-slate-200 p-5"
                data-testid="upcoming-highlights"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-[#E63946]">Highlights</span>
                  <Calendar className="w-4 h-4 text-slate-300" />
                </div>
                <ul className="space-y-3">
                  {upcomingChamber.slice(0, 4).map((ev) => {
                    const dt = formatDateShort(ev.date);
                    return (
                      <li key={ev.id}>
                        <button onClick={() => onSelect(ev)} className="w-full text-left flex items-start gap-3 group">
                          <span className="flex-shrink-0 w-11 h-11 rounded-lg bg-[#002249]/5 border border-[#002249]/10 flex flex-col items-center justify-center">
                            <span className="text-[14px] font-bold text-[#002249] leading-none">{dt.day}</span>
                            <span className="text-[9px] uppercase tracking-wider text-[#002249]/60 mt-0.5">{dt.month}</span>
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="text-[13px] font-semibold text-[#002249] line-clamp-1 group-hover:text-[#E63946] transition-colors block">{ev.title}</span>
                            <span className="text-[11px] text-slate-500 mt-0.5 line-clamp-1 block">{ev.time} · {ev.location}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            )}

            {/* External/Recommended teaser when not in external filter */}
            {filter !== 'external' && externalEvents.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-amber-50 border border-amber-200 rounded-2xl p-5"
              >
                <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-amber-700">Curated</span>
                <h3 className="text-[15px] font-bold text-[#002249] mt-1">{externalEvents.length} recommended events</h3>
                <p className="text-[12px] text-slate-600 mt-1.5">Worth knowing about — outside the DCCF calendar.</p>
                <button onClick={() => setFilter('external')} className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-[#002249] hover:underline" data-testid="goto-external-from-sidebar">
                  See all <ArrowRight className="w-3 h-3" />
                </button>
              </motion.div>
            )}
          </aside>
        </div>
      </div>

      {/* Leadership Signature */}
      <section className="py-12 sm:py-16 px-5 sm:px-8 bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto">
          <LeadershipSignature title="Board Director of Events" label="Events Team" blurb="Reach out to our Events Director to learn about upcoming Chamber gatherings, sponsorship opportunities, and event collaboration." />
        </div>
      </section>
    </div>
  );
};

/* ─── Sub-components ─────────────────────────────────────────────────────── */

const EmptyEventsState = ({ filter }) => (
  <div className="text-center py-14 px-6 bg-white rounded-2xl border border-slate-200" data-testid="events-empty">
    <div className="w-14 h-14 rounded-2xl bg-[#002249]/5 flex items-center justify-center mx-auto mb-4">
      <Calendar className="w-7 h-7 text-[#002249]/40" />
    </div>
    <p className="text-[15px] font-bold text-[#002249]">
      {filter === 'past' ? 'No past events to recap yet' : filter === 'external' ? 'No recommended events right now' : 'Nothing on the calendar yet'}
    </p>
    <p className="text-[13px] text-slate-500 mt-1.5">
      {filter === 'external'
        ? 'Curated picks will appear here as the DCCF team identifies them.'
        : 'Check back soon — new gatherings are announced every month.'}
    </p>
  </div>
);

const FeaturedCard = ({ event, onSelect, index, large }) => {
  const dt = formatDateShort(event.date);
  return (
    <motion.button
      onClick={() => onSelect(event)}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.08 }}
      whileHover={{ y: -4 }}
      className="relative text-left rounded-2xl overflow-hidden bg-[#002249] aspect-[4/5] sm:aspect-[3/4] group shadow-sm hover:shadow-2xl transition-shadow"
      data-testid={`featured-card-${index}`}
    >
      {(event.image_url || event.flyer_url) ? (
        <Img variant="event" src={resolveImg(event.image_url || event.flyer_url)} alt={event.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[800ms] group-hover:scale-110" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#002249] via-[#0a3570] to-[#E63946]" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
      <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-[#E63946] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md shadow-md">
        Featured
      </span>
      <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm text-[#002249] rounded-xl px-3 py-2 text-center shadow-md">
        <p className="text-[11px] uppercase tracking-wider font-semibold leading-none">{dt.month}</p>
        <p className="text-[22px] font-bold leading-none mt-1">{dt.day}</p>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 text-white">
        <h3 className={`font-bold tracking-tight leading-snug mb-2 ${large ? 'text-2xl sm:text-3xl' : 'text-lg sm:text-xl'}`}>{event.title}</h3>
        <div className="flex items-center gap-3 text-[12px] text-white/80">
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{event.time}</span>
          <span className="inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3" /><span className="truncate max-w-[160px]">{event.location}</span></span>
        </div>
        <span className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-amber-300 transition-all group-hover:gap-3">
          View details <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </motion.button>
  );
};

const UpcomingCard = ({ event, onSelect, index }) => {
  const dt = formatDateShort(event.date);
  return (
    <motion.button
      onClick={() => onSelect(event)}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
      whileHover={{ y: -3 }}
      className="text-left bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-[#002249]/30 hover:shadow-lg transition-all group"
      data-testid={`upcoming-card-${index}`}
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-[#002249] to-[#0a3570]">
        {(event.image_url || event.flyer_url) ? (
          <Img variant="event" src={resolveImg(event.image_url || event.flyer_url)} alt={event.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Calendar className="w-12 h-12 text-white/15" /></div>
        )}
        <span className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm text-[#002249] rounded-lg px-2.5 py-1.5 text-center">
          <span className="block text-[9px] uppercase tracking-wider font-semibold leading-none">{dt.month}</span>
          <span className="block text-[15px] font-bold leading-none mt-0.5">{dt.day}</span>
        </span>
      </div>
      <div className="p-4 sm:p-5">
        <h3 className="text-[15px] sm:text-[16px] font-bold text-[#002249] line-clamp-1 group-hover:text-[#E63946] transition-colors">{event.title}</h3>
        <p className="text-[12.5px] text-slate-500 mt-1 line-clamp-2 leading-relaxed">{event.description}</p>
        <div className="flex items-center justify-between mt-3.5 text-[11.5px]">
          <span className="text-slate-500 inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3" /><span className="truncate">{event.location}</span></span>
          <span className="font-semibold text-[#E63946] inline-flex items-center gap-1 group-hover:gap-2 transition-all">View <ArrowRight className="w-3 h-3" /></span>
        </div>
      </div>
    </motion.button>
  );
};

const ExternalEventCard = ({ event, index }) => {
  const dt = formatDateShort(event.date);
  const url = event.external_url || event.info_url || event.registration_link;
  const Wrapper = url ? motion.a : motion.div;
  const wrapperProps = url ? { href: url, target: '_blank', rel: 'noopener noreferrer' } : {};
  return (
    <Wrapper
      {...wrapperProps}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
      whileHover={{ y: -3 }}
      className="block bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-amber-400 hover:shadow-lg transition-all group cursor-pointer"
      data-testid={`external-card-${index}`}
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-amber-100 to-amber-200">
        {(event.image_url || event.flyer_url) ? (
          <Img variant="event" src={resolveImg(event.image_url || event.flyer_url)} alt={event.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Calendar className="w-12 h-12 text-amber-700/30" /></div>
        )}
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md shadow-sm">
          Recommended
        </span>
      </div>
      <div className="p-4 sm:p-5">
        <h3 className="text-[15px] font-bold text-[#002249] line-clamp-1 group-hover:text-amber-700 transition-colors">{event.title}</h3>
        <p className="text-[12.5px] text-slate-500 mt-1 line-clamp-2 leading-relaxed">{event.description}</p>
        <div className="flex items-center justify-between mt-3.5 text-[11.5px]">
          <span className="text-slate-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{dt.month} {dt.day}, {dt.year}</span>
          <span className="font-semibold text-amber-700 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
            Visit site <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Wrapper>
  );
};

const PastRecapCard = ({ event, onSelect, index }) => {
  const galleryCount = event.gallery_images?.length || 0;
  const dt = formatDateShort(event.date);
  return (
    <motion.button
      onClick={() => onSelect(event)}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
      whileHover={{ y: -3 }}
      className="text-left bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-lg transition-all group"
      data-testid={`past-card-${index}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#002249] to-[#457B9D]">
        {(event.image_url || event.flyer_url) ? (
          <Img variant="event" src={resolveImg(event.image_url || event.flyer_url)} alt={event.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Image className="w-14 h-14 text-white/15" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
        {galleryCount > 0 && (
          <span className="absolute top-3 right-3 bg-black/55 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1">
            <Camera className="w-3 h-3" />{galleryCount} photos
          </span>
        )}
        <div className="absolute bottom-3 left-3">
          <span className="text-white/90 text-[11px] font-medium tracking-wide">{dt.month} {dt.day}, {dt.year}</span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-bold text-[#002249] mb-1 line-clamp-1 group-hover:text-[#E63946] transition-colors text-[15px]">{event.title}</h3>
        <p className="text-[12.5px] text-slate-500 line-clamp-2 leading-relaxed">{event.recap || event.description}</p>
        <div className="flex items-center justify-between mt-3 text-[11.5px]">
          <span className="text-slate-500 inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3" /><span className="truncate">{event.location}</span></span>
          <span className="font-semibold text-[#E63946] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
            {galleryCount > 0 ? 'View album' : 'View recap'} <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </motion.button>
  );
};

export default EventsPage;
